import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  COLORS, Icons, Btn, Spinner, ErrorBlock, EmptyBlock, useToast,
} from '../ui/crm-ui';

interface Prompt {
  id: number;
  name: string;
  category: string | null;
  division: string | null;
  token_sensitivity: string | null;
  system_prompt: string;
  is_active: boolean;
  has_document_output: boolean;
  output_type: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  max_tokens: number | null;
}

interface MetaDraft {
  category: string;
  division: string;
  token_sensitivity: string;
  is_active: boolean;
}

const TOKEN_SENSITIVITY_OPTIONS = ['', 'Low', 'Medium', 'High'] as const;

interface HeaderCell { key: string; cell: string; }
interface DataColumn { header: string; key: string; width?: number; }
interface SheetConfigSheet {
  name: string;
  headerCells?: HeaderCell[];
  claudeKey: string;
  dataStartRow: number;
  writeColumnHeaders?: boolean;
  columns: DataColumn[];
}
interface SheetConfig { sheets: SheetConfigSheet[]; }
interface DocumentTemplate {
  template_id: number;
  template_name: string;
  sheet_config: SheetConfig;
  output_filename_prefix: string;
}

interface Props {
  baseApiUrl: string;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function cloneSheetConfig(cfg: SheetConfig): SheetConfig {
  return JSON.parse(JSON.stringify(cfg)) as SheetConfig;
}

function apiErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    return data?.error ?? err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

const PromptsView: React.FC<Props> = ({ baseApiUrl }) => {
  const toast = useToast();
  const [prompts, setPrompts] = useState<Prompt[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [maxTokensDrafts, setMaxTokensDrafts] = useState<Record<number, number | null>>({});
  const [metaDrafts, setMetaDrafts] = useState<Record<number, MetaDraft>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  // Document template state — keyed by prompt id
  const [templates, setTemplates] = useState<Record<number, DocumentTemplate>>({});
  const [templateDrafts, setTemplateDrafts] = useState<Record<number, SheetConfig>>({});
  const [templateLoading, setTemplateLoading] = useState<Record<number, boolean>>({});
  const [templateError, setTemplateError] = useState<Record<number, string | null>>({});
  const [templateMissing, setTemplateMissing] = useState<Record<number, boolean>>({});
  const [templateSavingId, setTemplateSavingId] = useState<number | null>(null);

  useEffect(() => {
    if (!baseApiUrl) return;
    let cancelled = false;
    axios
      .get<Prompt[]>(`${baseApiUrl}/api/prompts`)
      .then((res) => {
        if (cancelled) return;
        setPrompts(res.data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(apiErrorMessage(err));
      });
    return () => { cancelled = true; };
  }, [baseApiUrl]);

  function loadTemplate(p: Prompt) {
    if (templates[p.id] || templateLoading[p.id] || templateMissing[p.id]) return;
    setTemplateLoading((s) => ({ ...s, [p.id]: true }));
    setTemplateError((s) => ({ ...s, [p.id]: null }));
    axios
      .get<DocumentTemplate>(`${baseApiUrl}/api/prompts/${p.id}/template`)
      .then((res) => {
        setTemplates((t) => ({ ...t, [p.id]: res.data }));
        setTemplateDrafts((d) => ({ ...d, [p.id]: cloneSheetConfig(res.data.sheet_config) }));
      })
      .catch((err) => {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setTemplateMissing((s) => ({ ...s, [p.id]: true }));
        } else {
          setTemplateError((s) => ({ ...s, [p.id]: apiErrorMessage(err) }));
        }
      })
      .finally(() => {
        setTemplateLoading((s) => ({ ...s, [p.id]: false }));
      });
  }

  function toggleExpand(p: Prompt) {
    if (expandedId === p.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(p.id);
    setDrafts((d) => (p.id in d ? d : { ...d, [p.id]: p.system_prompt }));
    setMaxTokensDrafts((d) => (p.id in d ? d : { ...d, [p.id]: p.max_tokens ?? null }));
    setMetaDrafts((d) => (p.id in d ? d : { ...d, [p.id]: {
      category: p.category ?? '',
      division: p.division ?? '',
      token_sensitivity: p.token_sensitivity ?? '',
      is_active: p.is_active,
    } }));
    if (p.has_document_output) {
      loadTemplate(p);
    }
  }

  function updateDraft(id: number, value: string) {
    setDrafts((d) => ({ ...d, [id]: value }));
  }

  async function save(p: Prompt) {
    const draft = drafts[p.id] ?? p.system_prompt;
    if (!draft.trim()) {
      toast.show('Prompt cannot be empty');
      return;
    }
    const maxTokens = p.id in maxTokensDrafts ? maxTokensDrafts[p.id] : p.max_tokens ?? null;
    const meta = metaDrafts[p.id] ?? { category: p.category ?? '', division: p.division ?? '', token_sensitivity: p.token_sensitivity ?? '', is_active: p.is_active };
    setSavingId(p.id);
    try {
      const res = await axios.patch<Prompt>(
        `${baseApiUrl}/api/prompts/${p.id}`,
        {
          system_prompt: draft,
          max_tokens: maxTokens,
          category: meta.category || null,
          division: meta.division || null,
          token_sensitivity: meta.token_sensitivity || null,
          is_active: meta.is_active,
        },
      );
      setPrompts((list) => (list ? list.map((x) => (x.id === p.id ? res.data : x)) : list));
      setDrafts((d) => ({ ...d, [p.id]: res.data.system_prompt }));
      setMaxTokensDrafts((d) => ({ ...d, [p.id]: res.data.max_tokens ?? null }));
      setMetaDrafts((d) => ({ ...d, [p.id]: {
        category: res.data.category ?? '',
        division: res.data.division ?? '',
        token_sensitivity: res.data.token_sensitivity ?? '',
        is_active: res.data.is_active,
      } }));
      toast.show('Prompt saved');
    } catch (err) {
      toast.show(`Save failed: ${apiErrorMessage(err)}`);
    } finally {
      setSavingId(null);
    }
  }

  function reset(p: Prompt) {
    setDrafts((d) => ({ ...d, [p.id]: p.system_prompt }));
    setMaxTokensDrafts((d) => ({ ...d, [p.id]: p.max_tokens ?? null }));
    setMetaDrafts((d) => ({ ...d, [p.id]: {
      category: p.category ?? '',
      division: p.division ?? '',
      token_sensitivity: p.token_sensitivity ?? '',
      is_active: p.is_active,
    } }));
  }

  function updateHeaderCell(promptId: number, idx: number, cell: string) {
    setTemplateDrafts((d) => {
      const cur = d[promptId];
      if (!cur || cur.sheets.length === 0) return d;
      const sheet = cur.sheets[0];
      const headerCells = (sheet.headerCells ?? []).map((hc, i) =>
        i === idx ? { ...hc, cell } : hc,
      );
      return {
        ...d,
        [promptId]: { ...cur, sheets: [{ ...sheet, headerCells }, ...cur.sheets.slice(1)] },
      };
    });
  }

  function updateColumnHeader(promptId: number, idx: number, header: string) {
    setTemplateDrafts((d) => {
      const cur = d[promptId];
      if (!cur || cur.sheets.length === 0) return d;
      const sheet = cur.sheets[0];
      const columns = sheet.columns.map((c, i) => (i === idx ? { ...c, header } : c));
      return {
        ...d,
        [promptId]: { ...cur, sheets: [{ ...sheet, columns }, ...cur.sheets.slice(1)] },
      };
    });
  }

  function updateColumnWidth(promptId: number, idx: number, raw: string) {
    setTemplateDrafts((d) => {
      const cur = d[promptId];
      if (!cur || cur.sheets.length === 0) return d;
      const sheet = cur.sheets[0];
      const trimmed = raw.trim();
      const width = trimmed === '' ? undefined : Number(trimmed);
      const columns = sheet.columns.map((c, i) => {
        if (i !== idx) return c;
        const next = { ...c };
        if (width === undefined || Number.isNaN(width)) {
          delete next.width;
        } else {
          next.width = width;
        }
        return next;
      });
      return {
        ...d,
        [promptId]: { ...cur, sheets: [{ ...sheet, columns }, ...cur.sheets.slice(1)] },
      };
    });
  }

  function resetTemplate(promptId: number) {
    const orig = templates[promptId];
    if (!orig) return;
    setTemplateDrafts((d) => ({ ...d, [promptId]: cloneSheetConfig(orig.sheet_config) }));
  }

  async function saveTemplate(p: Prompt) {
    const draft = templateDrafts[p.id];
    if (!draft) return;
    setTemplateSavingId(p.id);
    try {
      const res = await axios.patch<DocumentTemplate>(
        `${baseApiUrl}/api/prompts/${p.id}/template`,
        { sheet_config: draft },
      );
      setTemplates((t) => ({ ...t, [p.id]: res.data }));
      setTemplateDrafts((d) => ({ ...d, [p.id]: cloneSheetConfig(res.data.sheet_config) }));
      toast.show('Template saved');
    } catch (err) {
      toast.show(`Save failed: ${apiErrorMessage(err)}`);
    } finally {
      setTemplateSavingId(null);
    }
  }

  return (
    <div className="crm-anim-fadeUp" style={{ fontFamily: '"Open Sans", system-ui, -apple-system, sans-serif' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: COLORS.dark }}>AI Prompts</h1>
        <p style={{ fontSize: 13, color: COLORS.mid, margin: '4px 0 0' }}>
          View and edit the system prompts used by the AI agents.
        </p>
      </div>

      {error && <ErrorBlock message={error} />}
      {!error && prompts == null && <Spinner />}
      {!error && prompts && prompts.length === 0 && (
        <EmptyBlock>No prompts have been configured yet.</EmptyBlock>
      )}

      {prompts && prompts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {prompts.map((p) => {
            const expanded = expandedId === p.id;
            const draft = drafts[p.id] ?? p.system_prompt;
            const maxTokensDraft = p.id in maxTokensDrafts ? maxTokensDrafts[p.id] : (p.max_tokens ?? null);
            const meta = metaDrafts[p.id] ?? { category: p.category ?? '', division: p.division ?? '', token_sensitivity: p.token_sensitivity ?? '', is_active: p.is_active };
            const dirty = draft !== p.system_prompt
              || maxTokensDraft !== (p.max_tokens ?? null)
              || (meta.category || null) !== p.category
              || (meta.division || null) !== p.division
              || (meta.token_sensitivity || null) !== p.token_sensitivity
              || meta.is_active !== p.is_active;
            const saving = savingId === p.id;

            return (
              <div
                key={p.id}
                style={{
                  background: COLORS.white,
                  borderRadius: 12,
                  border: `1px solid ${COLORS.border}`,
                  overflow: 'hidden',
                  transition: 'box-shadow 0.15s',
                  boxShadow: expanded ? '0 4px 16px rgba(18,38,170,0.06)' : 'none',
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(p)}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    background: 'transparent',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    textAlign: 'left',
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: COLORS.blueSoft, color: COLORS.blue,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {Icons.agent(COLORS.blue)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.dark }}>{p.name}</span>
                      {!p.is_active && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 6px',
                          borderRadius: 4, background: COLORS.light, color: COLORS.mid,
                          textTransform: 'uppercase', letterSpacing: 0.4,
                        }}>Inactive</span>
                      )}
                      {p.has_document_output && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 6px',
                          borderRadius: 4, background: COLORS.greenSoft, color: COLORS.green,
                          textTransform: 'uppercase', letterSpacing: 0.4,
                        }}>{(p.output_type ?? 'document').toUpperCase()} output</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.mid, marginTop: 2 }}>
                      {[p.category, p.division, p.token_sensitivity ? `${p.token_sensitivity} sensitivity` : null].filter(Boolean).join(' · ') || 'No category'}
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.mid, marginTop: 4 }}>
                      Last updated {formatDateTime(p.updated_at) || '—'}
                    </div>
                  </div>
                  <div style={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                    color: COLORS.mid,
                    flexShrink: 0,
                  }}>
                    {Icons.chevDown(COLORS.mid)}
                  </div>
                </button>

                {expanded && (
                  <div style={{
                    padding: '0 20px 20px',
                    borderTop: `1px solid ${COLORS.border}`,
                  }}>
                    {/* Metadata fields */}
                    <div style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
                      margin: '16px 0 4px',
                      padding: '14px 16px',
                      borderRadius: 8,
                      background: COLORS.pageBg,
                      border: `1px solid ${COLORS.border}`,
                    }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.mid, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          Category
                        </label>
                        <input
                          type="text"
                          value={meta.category}
                          onChange={(e) => setMetaDrafts((d) => ({ ...d, [p.id]: { ...meta, category: e.target.value } }))}
                          disabled={saving}
                          placeholder="e.g. Audit"
                          style={{ ...metaInputStyle, background: saving ? COLORS.light : COLORS.white }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.mid, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          Division
                        </label>
                        <input
                          type="text"
                          value={meta.division}
                          onChange={(e) => setMetaDrafts((d) => ({ ...d, [p.id]: { ...meta, division: e.target.value } }))}
                          disabled={saving}
                          placeholder="e.g. Audit"
                          style={{ ...metaInputStyle, background: saving ? COLORS.light : COLORS.white }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.mid, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          Token Sensitivity
                        </label>
                        <select
                          value={meta.token_sensitivity}
                          onChange={(e) => setMetaDrafts((d) => ({ ...d, [p.id]: { ...meta, token_sensitivity: e.target.value } }))}
                          disabled={saving}
                          style={{ ...metaInputStyle, background: saving ? COLORS.light : COLORS.white, cursor: 'pointer' }}
                        >
                          {TOKEN_SENSITIVITY_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt || '— Not set —'}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: COLORS.mid, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          Status
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: saving ? 'default' : 'pointer', userSelect: 'none' }}>
                          <input
                            type="checkbox"
                            checked={meta.is_active}
                            onChange={(e) => setMetaDrafts((d) => ({ ...d, [p.id]: { ...meta, is_active: e.target.checked } }))}
                            disabled={saving}
                            style={{ width: 15, height: 15, accentColor: COLORS.blue, cursor: saving ? 'default' : 'pointer' }}
                          />
                          <span style={{ fontSize: 13, color: meta.is_active ? COLORS.green : COLORS.mid, fontWeight: 600 }}>
                            {meta.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </label>
                      </div>
                    </div>

                    <label style={{
                      display: 'block', fontSize: 12, fontWeight: 600,
                      color: COLORS.mid, margin: '14px 0 6px',
                    }}>
                      System prompt
                    </label>
                    <textarea
                      value={draft}
                      onChange={(e) => updateDraft(p.id, e.target.value)}
                      disabled={saving}
                      spellCheck={false}
                      style={{
                        width: '100%',
                        minHeight: 280,
                        padding: '12px 14px',
                        borderRadius: 8,
                        border: `1px solid ${COLORS.border}`,
                        fontSize: 13,
                        fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                        lineHeight: 1.55,
                        outline: 'none',
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        background: saving ? COLORS.light : COLORS.white,
                        color: COLORS.dark,
                      }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = COLORS.blue; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = COLORS.border; }}
                    />
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      marginTop: 8, gap: 12, flexWrap: 'wrap',
                    }}>
                      <div style={{ fontSize: 11, color: COLORS.mid }}>
                        {draft.length.toLocaleString()} characters
                        {dirty && <span style={{ marginLeft: 8, color: COLORS.orange, fontWeight: 600 }}>· Unsaved changes</span>}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.mid, whiteSpace: 'nowrap' }}>
                        Max tokens
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={200000}
                        step={1024}
                        value={maxTokensDraft ?? ''}
                        onChange={(e) => {
                          const v = e.target.value.trim();
                          setMaxTokensDrafts((d) => ({ ...d, [p.id]: v === '' ? null : Number(v) }));
                        }}
                        disabled={saving}
                        placeholder="Default (4096)"
                        style={{
                          width: 160, padding: '6px 10px', borderRadius: 6,
                          border: `1px solid ${COLORS.border}`, fontSize: 13,
                          outline: 'none', background: saving ? COLORS.light : COLORS.white,
                          color: COLORS.dark, fontFamily: 'inherit',
                        }}
                      />
                      <span style={{ fontSize: 11, color: COLORS.mid }}>
                        Leave blank to use the default (4096). Increase for large documents — try 8192 or 16384.
                      </span>
                    </div>

                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      marginTop: 12, gap: 8,
                    }}>
                      <Btn
                        small
                        onClick={() => reset(p)}
                        disabled={!dirty || saving}
                      >
                        Reset
                      </Btn>
                      <Btn
                        small
                        primary
                        onClick={() => save(p)}
                        disabled={!dirty || saving}
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </Btn>
                    </div>

                    {p.has_document_output && (
                      <DocumentTemplateSection
                        prompt={p}
                        loading={!!templateLoading[p.id]}
                        missing={!!templateMissing[p.id]}
                        error={templateError[p.id] ?? null}
                        original={templates[p.id] ?? null}
                        draft={templateDrafts[p.id] ?? null}
                        saving={templateSavingId === p.id}
                        onUpdateHeaderCell={(idx, cell) => updateHeaderCell(p.id, idx, cell)}
                        onUpdateColumnHeader={(idx, header) => updateColumnHeader(p.id, idx, header)}
                        onUpdateColumnWidth={(idx, width) => updateColumnWidth(p.id, idx, width)}
                        onReset={() => resetTemplate(p.id)}
                        onSave={() => saveTemplate(p)}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {toast.node}
    </div>
  );
};

export default PromptsView;

// ─── Document Template subsection ──────────────────────────────────────────

interface DocumentTemplateSectionProps {
  prompt: Prompt;
  loading: boolean;
  missing: boolean;
  error: string | null;
  original: DocumentTemplate | null;
  draft: SheetConfig | null;
  saving: boolean;
  onUpdateHeaderCell: (idx: number, cell: string) => void;
  onUpdateColumnHeader: (idx: number, header: string) => void;
  onUpdateColumnWidth: (idx: number, width: string) => void;
  onReset: () => void;
  onSave: () => void;
}

const DocumentTemplateSection: React.FC<DocumentTemplateSectionProps> = ({
  loading, missing, error, original, draft, saving,
  onUpdateHeaderCell, onUpdateColumnHeader, onUpdateColumnWidth, onReset, onSave,
}) => {
  const dirty = !!(original && draft) &&
    JSON.stringify(draft) !== JSON.stringify(original.sheet_config);

  return (
    <div style={{
      marginTop: 22,
      paddingTop: 18,
      borderTop: `1px dashed ${COLORS.border}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 12, fontWeight: 700, color: COLORS.dark,
        textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 12,
      }}>
        Document Template
      </div>

      {loading && <Spinner />}
      {!loading && error && <ErrorBlock message={error} />}
      {!loading && !error && missing && (
        <EmptyBlock>This prompt has no active document template.</EmptyBlock>
      )}

      {!loading && !error && !missing && original && draft && draft.sheets.length > 0 && (() => {
        const sheet = draft.sheets[0];
        const headerCells = sheet.headerCells ?? [];
        const columns = sheet.columns;

        return (
          <>
            {/* Read-only metadata strip */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 14,
              padding: '10px 14px', marginBottom: 14,
              borderRadius: 8, background: COLORS.pageBg,
              border: `1px solid ${COLORS.border}`,
              fontSize: 11, color: COLORS.mid,
            }}>
              <span>Sheet: <strong style={{ color: COLORS.dark }}>{sheet.name}</strong></span>
              <span>Data starts at row <strong style={{ color: COLORS.dark }}>{sheet.dataStartRow}</strong></span>
              <span>Claude key: <code style={{ color: COLORS.dark, background: COLORS.white, padding: '1px 5px', borderRadius: 3 }}>{sheet.claudeKey}</code></span>
              <span>Output prefix: <strong style={{ color: COLORS.dark }}>{original.output_filename_prefix}</strong></span>
            </div>

            {/* Header Fields */}
            <div style={{ marginBottom: 18 }}>
              <div style={{
                fontSize: 12, fontWeight: 600, color: COLORS.mid, marginBottom: 6,
              }}>
                Header Fields
              </div>
              {headerCells.length === 0 ? (
                <div style={{ fontSize: 12, color: COLORS.mid, fontStyle: 'italic' }}>
                  No header fields configured.
                </div>
              ) : (
                <table style={{
                  width: '100%', borderCollapse: 'collapse',
                  border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden',
                }}>
                  <thead>
                    <tr style={{ background: COLORS.pageBg }}>
                      <th style={tableHeaderStyle}>Field Name</th>
                      <th style={{ ...tableHeaderStyle, width: 140 }}>Target Cell</th>
                    </tr>
                  </thead>
                  <tbody>
                    {headerCells.map((hc, idx) => (
                      <tr key={`${hc.key}-${idx}`}>
                        <td style={tableCellStyle}>
                          <code style={{ fontSize: 12, color: COLORS.dark }}>{hc.key}</code>
                        </td>
                        <td style={tableCellStyle}>
                          <input
                            type="text"
                            value={hc.cell}
                            onChange={(e) => onUpdateHeaderCell(idx, e.target.value.toUpperCase())}
                            disabled={saving}
                            spellCheck={false}
                            style={cellInputStyle}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Data Columns */}
            <div style={{ marginBottom: 14 }}>
              <div style={{
                fontSize: 12, fontWeight: 600, color: COLORS.mid, marginBottom: 6,
              }}>
                Data Columns
              </div>
              {columns.length === 0 ? (
                <div style={{ fontSize: 12, color: COLORS.mid, fontStyle: 'italic' }}>
                  No columns configured.
                </div>
              ) : (
                <table style={{
                  width: '100%', borderCollapse: 'collapse',
                  border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden',
                }}>
                  <thead>
                    <tr style={{ background: COLORS.pageBg }}>
                      <th style={tableHeaderStyle}>Column Header</th>
                      <th style={tableHeaderStyle}>Key</th>
                      <th style={{ ...tableHeaderStyle, width: 100 }}>Width</th>
                    </tr>
                  </thead>
                  <tbody>
                    {columns.map((col, idx) => (
                      <tr key={`${col.key}-${idx}`}>
                        <td style={tableCellStyle}>
                          <input
                            type="text"
                            value={col.header}
                            onChange={(e) => onUpdateColumnHeader(idx, e.target.value)}
                            disabled={saving}
                            style={textInputStyle}
                          />
                        </td>
                        <td style={tableCellStyle}>
                          <code style={{ fontSize: 12, color: COLORS.dark }}>{col.key}</code>
                        </td>
                        <td style={tableCellStyle}>
                          <input
                            type="number"
                            min={1}
                            value={col.width ?? ''}
                            onChange={(e) => onUpdateColumnWidth(idx, e.target.value)}
                            disabled={saving}
                            style={cellInputStyle}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap',
            }}>
              <div style={{ fontSize: 11, color: COLORS.mid }}>
                {dirty
                  ? <span style={{ color: COLORS.orange, fontWeight: 600 }}>· Unsaved changes</span>
                  : 'No changes'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn small onClick={onReset} disabled={!dirty || saving}>
                  Reset
                </Btn>
                <Btn small primary onClick={onSave} disabled={!dirty || saving}>
                  {saving ? 'Saving…' : 'Save Template'}
                </Btn>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
};

const metaInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  borderRadius: 6,
  border: `1px solid #e5e7eb`,
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  color: '#111827',
};

const tableHeaderStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 11,
  fontWeight: 600,
  color: COLORS.mid,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  borderBottom: `1px solid ${COLORS.border}`,
};

const tableCellStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 13,
  color: COLORS.dark,
  borderBottom: `1px solid ${COLORS.border}`,
};

const textInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  borderRadius: 6,
  border: `1px solid ${COLORS.border}`,
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  background: COLORS.white,
  color: COLORS.dark,
};

const cellInputStyle: React.CSSProperties = {
  ...textInputStyle,
  fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
};
